import { useEffect, useMemo, useRef } from 'react';

import { useFrontier } from '@raystack/frontier/react';
import {
  fetchCountryBbox,
  fetchCountryBboxFromCoords,
  getBrowserLocation
} from '@studio/utils';
import { isEmpty } from 'lodash';

import { useMapViewSelector } from '@src/contexts';
import { type CachedBboxData, getCachedBbox, setCachedBbox } from '@src/utils';

const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_TOKEN;

export function useActiveMapInitZoom() {
  const mapInstance = useMapViewSelector(store => store.mapInstance);

  const hasInitializedRef = useRef(false);

  const { billingAccount } = useFrontier();

  const country = billingAccount?.address?.country;

  const isBillingAccountLoaded = useMemo(() => {
    return !isEmpty(billingAccount);
  }, [billingAccount]);

  useEffect(() => {
    if (!mapInstance || hasInitializedRef.current) return;

    const abortController = new AbortController();

    async function initZoom() {
      if (!mapInstance) return;

      // Check localStorage cache first
      const { data: cached, isExpired } = getCachedBbox();

      // If cache exists and is not expired, use it directly
      if (cached && !isExpired) {
        hasInitializedRef.current = true;
        return;
      }

      if (!isBillingAccountLoaded) return;

      // Try to fetch fresh coordinates
      let freshBbox: CachedBboxData['bbox'] | null = null;

      // Case 1: Use billing country
      if (country) {
        // Skip fetching if billing country matches cached country
        if (cached && cached.country === country) {
          hasInitializedRef.current = true;
          return;
        }

        freshBbox = (await fetchCountryBbox({
          country,
          mapboxToken: MAPBOX_TOKEN,
          signal: abortController.signal
        })) as CachedBboxData['bbox'] | null;

        if (freshBbox && !abortController.signal.aborted) {
          hasInitializedRef.current = true;
          setCachedBbox(freshBbox, country);
          mapInstance.fitLayerBounds(freshBbox, { animate: false });
          return;
        }
      }

      // Case 2: Fallback to browser geolocation
      try {
        const position = await getBrowserLocation();
        if (abortController.signal.aborted) return;

        const { longitude, latitude } = position.coords;
        freshBbox = (await fetchCountryBboxFromCoords({
          lng: longitude,
          lat: latitude,
          mapboxToken: MAPBOX_TOKEN,
          signal: abortController.signal
        })) as CachedBboxData['bbox'] | null;

        if (freshBbox && !abortController.signal.aborted) {
          hasInitializedRef.current = true;

          // Skip cache update and map fit if bbox hasn't changed
          const isSameBbox =
            cached?.bbox &&
            freshBbox[0] === cached.bbox[0] &&
            freshBbox[1] === cached.bbox[1] &&
            freshBbox[2] === cached.bbox[2] &&
            freshBbox[3] === cached.bbox[3];

          if (!isSameBbox) {
            setCachedBbox(freshBbox, 'geolocation');
            mapInstance.fitLayerBounds(freshBbox, { animate: false });
          }

          return;
        }
      } catch (error) {
        // User denied permission or geolocation failed - silent fail
        console.error('Geolocation failed:', error);
      }

      // Case 3: If fresh fetch failed but we have expired cache, use it as fallback
      if (cached && !abortController.signal.aborted) {
        hasInitializedRef.current = true;
        return;
      }
    }

    initZoom();

    return () => {
      abortController.abort();
    };
  }, [mapInstance, country, isBillingAccountLoaded]);
}
